import {
  assoc,
  compose,
  concat,
  curry,
  dissoc,
  flatten,
  head,
  is,
  isEmpty,
  map,
  merge,
  omit,
  path,
  pathOr,
  pick,
  prop,
  reduce
} from 'ramda'
import { isBoolean } from '@btime/helpers'

import { cleanDynamicValueV2 } from '../../../../utils/helpers/dynamicInputs'

const normalizeAutofillForRequest = autofill => {
  if (!autofill || typeof autofill !== 'object') return autofill
  const source = autofill.source != null ? String(autofill.source).toLowerCase() : autofill.source
  const fieldId = autofill.fieldId ?? autofill.field_id ?? null
  const editable = autofill.editable !== false
  const updateBase = autofill.updateBase === true || autofill.update_base === true
  return {
    ...omit(['__typename'], autofill),
    source,
    fieldId,
    field_id: fieldId,
    editable,
    updateBase,
    update_base: updateBase
  }
}

const cleanDefaultValue = question => {
  if ([null, undefined].includes(question.defaultValue) || isEmpty(question.defaultValue)) {
    return assoc('defaultValue', null, question)
  }

  return compose(
    value => assoc('defaultValue', value, question), //
    value => cleanDynamicValueV2(value, question.questionType.name)
  )(question.defaultValue)
}

const cleanQuestionOptions = question =>
  assoc(
    'options',
    (question.options || []).filter(prop('name')).map(
      (option, index) =>
        compose(
          assoc('ordination', index), //
          pick(['id', 'name', 'reference', 'ordination'])
        )(option),
      question
    ),
    question
  )

const buildOriginalQuestionsById = checklist =>
  reduce((acc, q) => (q?.id ? { ...acc, [q.id]: q } : acc), {}, checklist?.questions || [])

const cleanChildrenQuestions = (categoryId, categoryOrdination, parentReference, originalById) => question =>
  concat(
    [question],
    map(cleanQuestion(categoryId, categoryOrdination, question.reference, originalById), question.children || [])
  )

const cleanConditions = conditions => {
  if (!conditions) return null
  conditions = conditions.filter(
    item => ![null, undefined].includes(item.value) && ![null, undefined].includes(item.operator)
  )
  if (!conditions.length) return null
  if (conditions.every(item => is(Array, item.value) && item.value?.every(prop('reference')))) {
    conditions = conditions.map(item => ({ ...item, value: item.value.map(prop('reference')) }))
  }

  return JSON.stringify(conditions)
}

const cleanExpectedCheckboxReply = question => {
  if (question.questionType?.name !== 'checkbox') return null
  const firstCondition = head(question.displayConditions || []) || {}
  if (!isBoolean(firstCondition.value)) return null
  return firstCondition.value
}

const cleanDocuments = compose(map(pick(['id', 'description'])), docs => (docs || []).filter(Boolean))

const cleanQuestion = curry((categoryId, categoryOrdination, parentReference, originalById, question) =>
  compose(
    cleanChildrenQuestions(categoryId, categoryOrdination, parentReference, originalById),
    pick([
      'id',
      'name',
      'categoryId',
      'categoryOrdination',
      'reference',
      'typeId',
      'typeName',
      'tagIds',
      'maskIds',
      'notesEnabled',
      'notesRequired',
      'private',
      'photosEnabled',
      'photosRequired',
      'photosFromGallery',
      'required',
      'expectedCheckboxReply',
      'allowNull',
      'documents',
      'description',
      'parentReference',
      'options',
      'questionOptionsId',
      'minPhotos',
      'nonComplianceConditions',
      'displayConditions',
      // To remove later
      'children',
      'logic',
      'defaultValue',
      'data'
    ]), //

    cleanQuestionOptions,
    question => (!!question.photosEnabled ? question : merge({ photosRequired: null }, question)),
    question => (!!question.notesEnabled ? question : merge({ notesRequired: null }, question)),
    question => assoc('parentReference', parentReference || null, question),
    question => assoc('minPhotos', question.minPhotos > 0 ? parseInt(question.minPhotos) : null, question),
    question => assoc('documents', cleanDocuments(question.documents || []), question),
    question => assoc('categoryOrdination', categoryOrdination, question),
    question => assoc('typeId', question.questionType.id, question),
    question => assoc('typeName', question.questionType.name, question),
    question => assoc('tagIds', (question.tags || []).map(prop('id')), question),
    question => {
      const masks = pathOr([], ['masks'], question)
      return assoc('maskIds', [...(!Array.isArray(masks) ? [masks] : masks)]?.map(prop('id')), question)
    },
    item => assoc('configuration', item.configuration ? JSON.stringify(item.configuration) : null, item),
    question => cleanDefaultValue(question),
    question => assoc('nonComplianceConditions', cleanConditions(question.nonComplianceConditions), question),
    question => assoc('displayConditions', cleanConditions(question.displayConditions), question),
    question => assoc('expectedCheckboxReply', cleanExpectedCheckboxReply(question), question),
    question => {
      const fromForm = pathOr(null, ['data', 'autofill'], question)
      const original = originalById?.[question.id]
      const fromOriginal = original?.autofill ?? pathOr(null, ['data', 'autofill'], original)
      const autofillValue = fromForm ?? fromOriginal
      const normalized = autofillValue != null ? normalizeAutofillForRequest(autofillValue) : null
      return normalized != null ? assoc('autofill', normalized, question) : dissoc('autofill', question)
    },
    question => assoc('data', JSON.stringify(question.data || {}), question),
    assoc('categoryId', categoryId)
  )(question)
)

export const cleanQuestions = (categories, originalById = {}) =>
  compose(
    questions =>
      questions.map((question, index) =>
        compose(assoc('ordination', index), dissoc('children'), dissoc('logic'))(question)
      ),
    flatten,
    cats =>
      cats.map((category, index) =>
        category.children.map(cleanQuestion(category.selectedCategory.id, index, undefined, originalById))
      )
  )(categories)

export const cleanRequest = (form, checklist = null) => {
  const originalById = buildOriginalQuestionsById(checklist)
  return compose(
    pick([
      'id',
      'questions',
      'typeId',
      'name',
      'allowReview',
      'allowBulkExecution',
      'requireCheckin',
      'emailNotificationEnabled',
      'usages',
      'description',
      'icon',
      'category',
      'runtime',
      'configuration'
    ]),
    item => assoc('questions', cleanQuestions(item.categories, originalById), item),
    item => assoc('typeId', pathOr(null, ['type', 'id'], item), item),
    item => assoc('configuration', JSON.stringify(item.configuration), item)
  )(form)
}
